import { Reacts, Variable, Div, Span, ElementProperties, all, VBoolean } from 'alkali'
import { Annotation } from './model/Annotations'
import AnnotationMark from 'components/extract/AnnotationMark'

export const TextSpan = Span.with('.span')

export interface AnnotationModel {
	readonly hideRemoteAnnotations?: VBoolean
	createTextSpan(text: string, textOffset: number, annotEls: Map<Annotation, Element>): Element
	createAnnotationSpan(annotation: Annotation, annotEls: Map<Annotation, Element>): Element
	canSelect(): boolean
	handleSelection(selected: string, offset: number, end: number, overlapped: Annotation[])
}

/**
 * Default non-interactive readonly AnnotationModel that disallows deletion and disables selection
 */
class DefaultAnnotationModel implements AnnotationModel {
	createTextSpan(text: string, textOffset: number, annotEls: Map<Annotation, Element>): Element {
		return new TextSpan({ textOffset }, text)
	}
	createAnnotationSpan(annotation: Annotation, annotEls: Map<Annotation, Element>): Element {
		if (annotation.interactive != false) {
			return new AnnotationMark({
				textOffset: annotation.start,
				annot: annotation,
				annotEls,
				readOnly: true
			})
		} else {
			// for now don't allow interaction with "labels"
			return new TextSpan(`.dre-ds-ann${annotation.type ? `.${annotation.type}` : ''}`, { textOffset: annotation.start }, annotation.text)
		}
	}
	canSelect(): boolean {
		return false
	}
	handleSelection(selected: string, offset: number, end: number, overlapped: Annotation[]) {
		throw new Error('handleSelection not implemented')
	}
}

export interface AnnotatableTextOpts extends ElementProperties<AnnotatableText> {
	text: Variable<string>
	annotations: Variable<Annotation[]>
	model: AnnotationModel
}

/**
 * Renders text with annotations applied. Annotations may be deleted, and new annotations created via text-range selections,
 * governed by the AnnotationModel
 */
export default class AnnotatableText extends Div('.annotated-text.') {
	private readonly text: Variable<string>
	private readonly annotations: Reacts<Annotation[]>
	private annotElements: Map<Annotation, Element>
	private model: AnnotationModel

	created(props: AnnotatableTextOpts) {
		this.annotElements = new Map<Annotation, Element>()
		this.model = props.model || new DefaultAnnotationModel()
		props.content = all([props.annotations, this.model.hideRemoteAnnotations, props.text], (annotations, hideRemoteAnnotations, text) =>
			this.renderAnnotations(annotations, text)
		)
	}

	private renderAnnotations(annotations, text) {
		if (text.trim().length == 0) {
			return [Div('.empty-abstract', 'Abstract not available')]
		}
		this.annotElements.clear()
		const els = []
		let position = 0
		for (const annotation of annotations) {
			const txt = text.slice(position, annotation.start)
			if (txt.length > 0) {
				els.push(this.model.createTextSpan(txt, position, this.annotElements))
			}
			const el = this.model.createAnnotationSpan(annotation, this.annotElements)
			this.annotElements.set(annotation, el)
			els.push(el)
			position = annotation.end + 1
		}
		const txt = text.slice(position)
		if (txt.length > 0) {
			els.push(this.model.createTextSpan(txt, position,  this.annotElements))
		}
		return els
	}

	private locateBound(node, selectionOffset) {
		const nearestEl = node.nodeType == 3 ? node.parentElement : node
		const el = nearestEl.closest('.span')
		if (el) {
			return {
				el,
				textOffset: el.textOffset,
				selectionOffset: el.textOffset + selectionOffset,
				annot: el.annot
			}
		}
	}

	private handleSelection(selection: Selection) {
		let startBound = this.locateBound(selection.anchorNode, selection.anchorOffset)
		let endBound = this.locateBound(selection.focusNode, selection.focusOffset)
		if (startBound == null && endBound == null) return
		if (!startBound != !endBound) {
			const backfillBound = (existing, existingNode, otherNode) => ({
				...existing,
				selectionOffset: (existingNode.compareDocumentPosition(otherNode) & 2) ? existing.textOffset
																					   : existing.textOffset + existing.el.textContent.length
			})
			startBound ? endBound = backfillBound(startBound, selection.anchorNode, selection.focusNode)
					   : startBound = backfillBound(endBound, selection.focusNode, selection.anchorNode)
		}
		if (startBound.selectionOffset > endBound.selectionOffset) {
			// user selected in reverse
			[startBound, endBound] = [endBound, startBound]
		}
		console.log('Selection: start:', startBound, 'end:', endBound)
		selection.removeAllRanges()

		// trim surrounding space
		let startOffset = startBound.selectionOffset
		let endOffset = endBound.selectionOffset
		const text = this.text.valueOf()
		let selected = text.slice(startBound.selectionOffset, endBound.selectionOffset)
		let m = /^\s+/.exec(selected)
		if (m) {
			startOffset += m[0].length
			selected = selected.slice(m[0].length)
		}
		m = /\s+$/.exec(selected)
		if (m) endOffset -= m[0].length
		if (startOffset > endOffset - 1) {
			// empty selection, bail
			return
		}
		// now walk backwards marking any overlapped annotations as deleted
		let end = endBound.el
		const overlappedAnnotations = []
		while (true) {
			console.log(end)
			if (end.annot) {
				// TODO: we could possibly split the annotation at selection boundary instead of just removing it entirely
				overlappedAnnotations.push(end.annot)
			}
			if (end == startBound.el) break
			end = end.previousElementSibling
		}

		console.log('Selection: start:', startBound, 'end:', endBound, 'selected', text.slice(startOffset, endOffset), 'overlapped:', overlappedAnnotations)
		this.model.handleSelection(text.slice(startOffset, endOffset), startOffset, endOffset, overlappedAnnotations)
	}

	onmouseup = function() {
		const self = this as AnnotatableText
		if (!self.model.canSelect()) return
		const selection = window.getSelection()
		if (selection.anchorNode == selection.focusNode &&
		    selection.anchorOffset == selection.focusOffset) {
			// there isn't a selection
			return
		}
		self.handleSelection(selection)
	}
}